/**
 * Malachyte decides only the ORDER of a collection's products — nothing else.
 * Every product card on the page is rendered by Liquid; this module never
 * builds markup, and it only ever asks Malachyte for a list of product ids.
 *
 * Flow:
 *   1. Shopify renders one paginated page of cards server-side (SEO + no-JS).
 *   2. On connect we start two requests in parallel — the remaining Liquid
 *      section pages (for their cards) and the Malachyte listing (for the ids).
 *   3. When both land we reorder the collected cards to match Malachyte's ids
 *      and hand them back `pageSize` at a time, entirely client-side.
 *   4. A shimmer covers the grid until step 3 completes.
 *
 * Any failure at any step reveals Shopify's native order, untouched, and hands
 * pagination back to `results-list`.
 */

import { sectionRenderer } from "@theme/section-renderer";
import { initLazyImages } from "@theme/lazyImages";

const LOG = "[Malachyte PLP]";
const PENDING_ATTRIBUTE = "data-malachyte-pending";
const REQUEST_TIMEOUT_MS = 20000;
const PAGE_FETCH_CONCURRENCY = 4;

/**
 * Reordering the whole collection means loading the whole collection, so the
 * page budget is the real limit here. Past it we leave the PLP on Shopify's
 * order rather than fire a dozen-plus section requests at every visitor.
 */
const MAX_COLLECTION_PAGES = 12;
const MAX_RANKED_PRODUCTS = 500;
const BOT_UA_RE = /bot|crawler|spider|slurp|bingpreview|facebookexternalhit|lighthouse|gtmetrix/i;

let malachyteApiPromise;

const now = () => (typeof performance !== "undefined" ? performance.now() : Date.now());
const ms = value => (value == null ? null : Math.round(value * 10) / 10);

/** @param {string} url */
function loadMalachyteApi(url) {
  malachyteApiPromise ||= import(url).then(module => module.malachyteAPI);
  return malachyteApiPromise;
}

/**
 * Pull a numeric Shopify product id out of whatever shape Malachyte returns.
 * @param {any} item
 * @returns {string|null}
 */
function itemId(item) {
  const raw = item?.facts?.id ?? item?.product?.id ?? item?.product_id ?? item?.productId ?? item?.id ?? null;
  if (raw == null) return null;

  const normalized = String(raw)
    .replace(/^gid:\/\/shopify\/Product\//, "")
    .replace(/^shopify:\/\/Product\//, "")
    .trim();

  return /^\d+$/.test(normalized) ? normalized : null;
}

function isBot() {
  return typeof navigator === "undefined" || BOT_UA_RE.test(navigator.userAgent || "");
}

/**
 * @typedef {{node: HTMLElement, isProduct: boolean}} Slot
 * @typedef {{id: string, el: HTMLElement}} ProductEntry
 * @typedef {{key: string, position: number, els: HTMLElement[]}} CalloutEntry
 * @typedef {{products: ProductEntry[], callouts: CalloutEntry[]}} Harvest
 */

class MalachytePlp extends HTMLElement {
  /** @type {AbortController|null} */
  #requestController = null;

  #requestToken = 0;

  /** Malachyte owns the grid and the Load More button. */
  #active = false;

  /** @type {Slot[]} Assembled render order: ranked products with callouts pinned to fixed slots. */
  #slots = [];

  /** Index of the next unrendered slot. */
  #slotCursor = 0;

  /** Product cards handed to the grid so far. */
  #renderedProducts = 0;

  #totalProducts = 0;

  /** Client-side page currently shown. */
  #page = 0;

  /** @type {HTMLElement|null} */
  #resultsList = null;

  /** @type {IntersectionObserver|null} Our replacement for the native infinite scroll. */
  #autoLoadObserver = null;

  /** Re-entrancy guard for the auto-load loop. */
  #autoLoading = false;

  connectedCallback() {
    this.collectionId = this.dataset.collectionId || "";
    this.componentId = this.dataset.componentId || "";
    this.catalog = this.dataset.catalog || "";
    this.apiUrl = this.dataset.apiUrl || "";
    this.sectionId = this.dataset.sectionId || "";
    this.pageSize = Math.max(1, Number.parseInt(this.dataset.pageSize || "24", 10));
    this.productsCount = Math.max(0, Number.parseInt(this.dataset.productsCount || "0", 10));
    this.totalPages = Math.max(1, Number.parseInt(this.dataset.totalPages || "1", 10));
    this.serverPage = Math.min(this.totalPages, Math.max(1, Number.parseInt(this.dataset.currentPage || "1", 10)));

    this.#resultsList = this.closest("results-list");

    if (!this.collectionId || !this.componentId || !this.catalog || !this.apiUrl || !this.sectionId || isBot()) {
      this.#reveal();
      return;
    }

    this.#resultsList?.addEventListener("click", this.#onLoadMoreClick, true);
    document.addEventListener("section:morph", this.#onSectionMorph);
    document.addEventListener("filter:update", this.#onFilterUpdate);
    this.#run();
  }

  disconnectedCallback() {
    this.#resultsList?.removeEventListener("click", this.#onLoadMoreClick, true);
    document.removeEventListener("section:morph", this.#onSectionMorph);
    document.removeEventListener("filter:update", this.#onFilterUpdate);
    this.#requestController?.abort();
    this.#stopAutoLoad();
  }

  /**
   * Shimmer from the moment the shopper clears the last filter.
   *
   * `filter:update` fires before the section request goes out, and the URL is
   * already rewritten by then (both facets dispatch sites pushState first), so
   * the existing filter/sort checks decide whether we are about to re-rank.
   *
   * Without this the grid stays live through the whole request and then morphs
   * in Shopify's order, which reads as products loading normally and then being
   * replaced when the ranking lands a couple of seconds later. Hiding here makes
   * it one uninterrupted shimmer: request, morph, re-rank, reveal.
   */
  #onFilterUpdate = event => {
    const form = event?.target instanceof Element ? event.target.closest("facets-form-component") : null;
    if (form && form.getAttribute("section-id") !== this.sectionId) return;

    // Filtered or explicitly sorted views keep Shopify's order — nothing to wait for.
    if (this.#hasActiveShopifyFilters() || this.#hasExplicitShopifySort()) return;

    this.#hide();
  };

  #onSectionMorph = event => {
    if (event?.detail?.sectionId !== this.sectionId) return;

    const host = this.closest("results-list");
    if (host !== this.#resultsList) {
      this.#resultsList?.removeEventListener("click", this.#onLoadMoreClick, true);
      host?.addEventListener("click", this.#onLoadMoreClick, true);
      this.#resultsList = host;
    }

    this.#run();
  };

  #onLoadMoreClick = event => {
    if (!this.#active || !(event.target instanceof Element)) return;

    const button = event.target.closest('[ref="loadMoreButton"]');
    if (!(button instanceof HTMLButtonElement) || !this.#resultsList?.contains(button)) return;

    // Beat results-list's own on:click handler — everything we need is already in memory.
    event.preventDefault();
    event.stopImmediatePropagation();

    const grid = this.#gridList();
    if (!grid) return;

    this.#renderClientPage(grid);
    this.#updateLoadMoreButton();
    this.#announceRender();
  };

  #hasActiveShopifyFilters() {
    return [...new URLSearchParams(window.location.search).keys()].some(key => key.startsWith("filter."));
  }

  #hasExplicitShopifySort() {
    const sortBy = new URLSearchParams(window.location.search).get("sort_by");
    return Boolean(sortBy && sortBy !== "manual");
  }

  /** @returns {HTMLUListElement|null} */
  #gridList() {
    const container = document.getElementById(`products-container-${this.sectionId}`);
    const grid = container?.querySelector('[ref="grid"]');
    return grid instanceof HTMLUListElement ? grid : null;
  }

  /** Swap the shimmer out for the grid. Safe to call more than once. */
  #reveal() {
    (this.#resultsList ?? this.closest("results-list"))?.removeAttribute(PENDING_ATTRIBUTE);

    // Belt and braces: any Swiper that did initialise while hidden measured zero width.
    requestAnimationFrame(() => {
      const container = document.getElementById(`products-container-${this.sectionId}`);
      for (const el of container?.querySelectorAll(".productCard__swiper") ?? []) {
        /** @type {any} */ (el).swiper?.update();
      }
    });
  }

  #hide() {
    (this.#resultsList ?? this.closest("results-list"))?.setAttribute(PENDING_ATTRIBUTE, "");
  }

  async #run() {
    this.#requestController?.abort();
    this.#stopAutoLoad();
    this.#active = false;
    this.#slots = [];
    this.#slotCursor = 0;
    this.#renderedProducts = 0;
    this.#totalProducts = 0;
    this.#page = 0;

    // An explicit filter or sort is the shopper's own intent — never reorder it.
    if (this.#hasActiveShopifyFilters() || this.#hasExplicitShopifySort()) {
      this.#reveal();
      return;
    }

    const grid = this.#gridList();
    if (!grid) {
      this.#reveal();
      return;
    }

    if (this.totalPages > MAX_COLLECTION_PAGES) {
      console.warn(
        `${LOG} collection spans ${this.totalPages} pages (limit ${MAX_COLLECTION_PAGES}); native Shopify order retained`
      );
      this.#reveal();
      return;
    }

    this.#hide();

    const requestToken = ++this.#requestToken;
    const requestController = new AbortController();
    this.#requestController = requestController;
    const timer = window.setTimeout(() => requestController.abort(), REQUEST_TIMEOUT_MS);
    const container = document.getElementById(`products-container-${this.sectionId}`);
    container?.setAttribute("aria-busy", "true");

    const startedAt = now();
    /** @type {{liquidPages: number|null, malachyteListing: number|null, reorderRender: number|null, shimmer: number|null}} */
    const timings = { liquidPages: null, malachyteListing: null, reorderRender: null, shimmer: null };

    try {
      const [harvest, rankedIds] = await Promise.all([
        this.#collectCards(grid).then(result => {
          timings.liquidPages = now() - startedAt;
          return result;
        }),
        this.#fetchRankedIds(requestController.signal).then(result => {
          timings.malachyteListing = now() - startedAt;
          return result;
        }),
      ]);

      if (!this.isConnected || requestToken !== this.#requestToken) return;
      if (rankedIds.length === 0) throw new Error("Malachyte returned no product ids");
      if (harvest.products.length === 0) throw new Error("No Liquid product cards to reorder");

      const renderStartedAt = now();
      const shopifyOrder = harvest.products.map(product => product.id);

      this.#buildSlots(harvest, rankedIds);

      grid.replaceChildren();
      this.#reveal();

      // Deep links land on ?page=N — keep the same amount of content on screen.
      for (let page = 0; page < this.serverPage; page++) this.#renderClientPage(grid);

      timings.reorderRender = now() - renderStartedAt;
      timings.shimmer = now() - startedAt;

      this.#active = true;
      this.#suspendNativePagination();
      this.#updateLoadMoreButton();
      this.#startAutoLoad();
      this.#report(shopifyOrder, rankedIds, timings);
      this.#announceRender({ rankedCount: rankedIds.length, timings });
    } catch (error) {
      if (requestToken === this.#requestToken) {
        this.#active = false;
        window.__malachyteQA__ = { enabled: true, applied: false, reason: String(error?.message ?? error) };
        console.warn(`${LOG} native Shopify order retained`, error);
      }
    } finally {
      window.clearTimeout(timer);
      if (requestToken === this.#requestToken) {
        container?.removeAttribute("aria-busy");
        this.#requestController = null;
        this.#reveal();
      }
    }
  }

  /**
   * Ask Malachyte for the whole collection's ranking in one request.
   * @param {AbortSignal} signal
   * @returns {Promise<string[]>} Unique numeric product ids, ranked
   */
  async #fetchRankedIds(signal) {
    const k = Math.min(Math.max(this.productsCount, this.pageSize), MAX_RANKED_PRODUCTS);
    const malachyteAPI = await loadMalachyteApi(this.apiUrl);
    const { results } = await malachyteAPI.listing(this.collectionId, {
      componentId: this.componentId,
      catalog: this.catalog,
      k,
      signal,
    });

    const ids = [];
    const seen = new Set();
    for (const result of results) {
      const id = itemId(result);
      if (!id || seen.has(id)) continue;
      seen.add(id);
      ids.push(id);
    }

    return ids;
  }

  /**
   * Gather every product card in the collection: the server-rendered page from
   * the live DOM, the rest from background section requests.
   * @param {HTMLUListElement} grid
   * @returns {Promise<Harvest>}
   */
  async #collectCards(grid) {
    /** @type {Harvest[]} */
    const byPage = new Array(this.totalPages);
    byPage[this.serverPage - 1] = this.#harvest(grid, true);

    const pending = [];
    for (let page = 1; page <= this.totalPages; page++) {
      if (page !== this.serverPage) pending.push(page);
    }

    for (let index = 0; index < pending.length; index += PAGE_FETCH_CONCURRENCY) {
      const chunk = pending.slice(index, index + PAGE_FETCH_CONCURRENCY);
      const documents = await Promise.all(chunk.map(page => this.#fetchSectionPage(page)));

      chunk.forEach((page, offset) => {
        const list = documents[offset]?.querySelector('[ref="grid"]');
        byPage[page - 1] = list instanceof HTMLElement ? this.#harvest(list, false) : { products: [], callouts: [] };
      });
    }

    // Flatten in page order so unranked products keep Shopify's natural sequence.
    /** @type {Harvest} */
    const harvest = { products: [], callouts: [] };
    const seenProducts = new Set();
    const seenCallouts = new Set();

    for (const page of byPage) {
      for (const product of page?.products ?? []) {
        if (seenProducts.has(product.id)) continue;
        seenProducts.add(product.id);
        harvest.products.push(product);
      }
      for (const callout of page?.callouts ?? []) {
        if (seenCallouts.has(callout.key)) continue;
        seenCallouts.add(callout.key);
        harvest.callouts.push(callout);
      }
    }

    return harvest;
  }

  /**
   * @param {number} page
   * @returns {Promise<Document>}
   */
  async #fetchSectionPage(page) {
    const url = new URL(window.location.href);
    url.searchParams.set("page", String(page));
    url.searchParams.delete("section_id");

    const html = await sectionRenderer.getSectionHTML(this.sectionId, true, url);
    return new DOMParser().parseFromString(html, "text/html");
  }

  /**
   * Split a grid's children into product cards and positioned callouts.
   * Nodes from a parsed document are imported so they can be inserted later;
   * live nodes are reused as-is to keep their already-painted images.
   * @param {HTMLElement} list
   * @param {boolean} isLive
   * @returns {Harvest}
   */
  #harvest(list, isLive) {
    /** @type {Harvest} */
    const harvest = { products: [], callouts: [] };
    const adopt = node => (isLive ? node : document.importNode(node, true));

    for (const child of [...list.children]) {
      if (!(child instanceof HTMLElement)) continue;

      if (child.dataset.productId) {
        harvest.products.push({ id: String(child.dataset.productId), el: adopt(child) });
        continue;
      }

      const position = Number.parseInt(child.dataset.calloutPosition || "", 10);
      if (!Number.isInteger(position) || position < 1) continue;

      const els = [adopt(child)];
      const sibling = child.nextElementSibling;
      if (sibling instanceof HTMLElement && sibling.classList.contains("product-grid__divider")) {
        els.push(adopt(sibling));
      }

      harvest.callouts.push({ key: child.id || `${position}:${harvest.callouts.length}`, position, els });
    }

    return harvest;
  }

  /**
   * Order the product cards by Malachyte's ids, then pin callouts back to their
   * absolute grid slots (position N sits immediately before the Nth product).
   * @param {Harvest} harvest
   * @param {string[]} rankedIds
   */
  #buildSlots(harvest, rankedIds) {
    const byId = new Map(harvest.products.map(product => [product.id, product]));

    /** @type {HTMLElement[]} */
    const ordered = [];
    const placed = new Set();

    for (const id of rankedIds) {
      const product = byId.get(id);
      if (!product || placed.has(id)) continue;
      placed.add(id);
      ordered.push(product.el);
    }

    // Anything Malachyte didn't rank keeps Shopify's order at the tail.
    for (const product of harvest.products) {
      if (!placed.has(product.id)) ordered.push(product.el);
    }

    /** @type {Map<number, HTMLElement[]>} */
    const calloutsByPosition = new Map();
    for (const callout of harvest.callouts) {
      const bucket = calloutsByPosition.get(callout.position) ?? [];
      bucket.push(...callout.els);
      calloutsByPosition.set(callout.position, bucket);
    }

    /** @type {Slot[]} */
    const slots = [];
    ordered.forEach((node, index) => {
      for (const callout of calloutsByPosition.get(index + 1) ?? []) {
        slots.push({ node: callout, isProduct: false });
      }
      slots.push({ node, isProduct: true });
    });

    // Callouts positioned past the last product still belong at the end.
    const trailing = [...calloutsByPosition.keys()].filter(position => position > ordered.length).sort((a, b) => a - b);
    for (const position of trailing) {
      for (const callout of calloutsByPosition.get(position) ?? []) {
        slots.push({ node: callout, isProduct: false });
      }
    }

    this.#slots = slots;
    this.#totalProducts = ordered.length;
  }

  /**
   * Append the next `pageSize` products (plus any callouts between them).
   * @param {HTMLUListElement} grid
   */
  #renderClientPage(grid) {
    if (this.#slotCursor >= this.#slots.length) return;

    const page = this.#page + 1;
    const target = page * this.pageSize;
    const fragment = document.createDocumentFragment();

    const push = slot => {
      slot.node.hidden = false;
      slot.node.dataset.page = String(page);
      fragment.appendChild(slot.node);
    };

    while (this.#slotCursor < this.#slots.length && this.#renderedProducts < target) {
      const slot = this.#slots[this.#slotCursor++];
      push(slot);
      if (slot.isProduct) this.#renderedProducts++;
    }

    // Trailing callouts belong to the page they follow, not the next one.
    while (this.#slotCursor < this.#slots.length && !this.#slots[this.#slotCursor].isProduct) {
      push(this.#slots[this.#slotCursor++]);
    }

    grid.appendChild(fragment);
    this.#page = page;

    initLazyImages();
  }

  /**
   * Hand pagination fully over to this component.
   *
   * `results-list` reaches its own #renderNextPage() by two routes, and both
   * append a second copy of a Shopify page underneath the ranked grid:
   *
   * 1. An IntersectionObserver on the `viewMoreNext` sentinel. Disconnecting
   *    handles the live one; removing the sentinels makes it stick, because
   *    #observeViewMore() bails when neither ref exists and it can be called
   *    again (on connect, and on filter update).
   *
   * 2. The Load More button's `on:click="/handleLoadMore"`. component.js
   *    delegates that from `document` in the *capture* phase, so it fires
   *    before any listener we add further down the tree — our capture
   *    listener on `results-list` cannot stopImmediatePropagation() in time.
   *    Dropping the attribute is the only reliable way to win the race: with
   *    no `[on:click]` on the button and none on its ancestors, the delegated
   *    handler resolves no element and returns. Our own listener still fires.
   */
  #suspendNativePagination() {
    const host = /** @type {any} */ (this.#resultsList);
    if (!host) return;

    host.infinityScrollObserver?.disconnect();

    for (const name of ["viewMoreNext", "viewMorePrevious"]) {
      host.querySelector(`[ref="${name}"]`)?.remove();
    }

    host.querySelector('[ref="loadMoreButton"]')?.removeAttribute("on:click");

    // `results-list` re-checks Load More visibility from a grid MutationObserver
    // — which our own appends trip — and decides by reading the *last* card's
    // `data-page` against `data-last-page`. Once reordered, the last card in the
    // DOM comes from an arbitrary Shopify page, so that check is meaningless and
    // hides the button while ranked pages are still queued. Dropping
    // `data-last-page` makes the comparison NaN, so it never hides anything and
    // #updateLoadMoreButton() stays the single source of truth. Safe because
    // #shouldUsePage() then refuses every native page fetch too.
    this.#gridList()?.removeAttribute("data-last-page");
  }

  /**
   * Replace the native infinite scroll we just suspended.
   *
   * Same trigger as the theme's own (`rootMargin: 100px`), but it pages from
   * `#slots` in memory instead of fetching, so there is no request and no
   * chance of appending a Shopify page on top of the ranked order.
   *
   * The Load More container is the target rather than a sentinel of our own:
   * it already sits after the grid, and #updateLoadMoreButton() hides it on
   * the last page, which drops it out of intersection and quiets the observer.
   */
  #startAutoLoad() {
    this.#stopAutoLoad();

    if (typeof IntersectionObserver === "undefined") return;

    const target = this.#resultsList?.querySelector('[ref="loadMoreContainer"]');
    if (!(target instanceof HTMLElement)) return;

    this.#autoLoadObserver = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) this.#autoLoad();
      },
      { rootMargin: "100px" }
    );
    this.#autoLoadObserver.observe(target);
  }

  #stopAutoLoad() {
    this.#autoLoadObserver?.disconnect();
    this.#autoLoadObserver = null;
    this.#autoLoading = false;
  }

  /**
   * Render the next client page, then keep going while the trigger is still in
   * view. The follow-up check is needed because an element that stays
   * continuously intersecting produces no further observer entries — without it
   * a short page would stall until the shopper scrolled again. Bounded by
   * `#slots`: every pass advances `#slotCursor`, and exhaustion stops the loop.
   *
   * The follow-up is a timer, not requestAnimationFrame: rAF is suspended while
   * the page is not painting (backgrounded tab, prerender), which would leave
   * `#autoLoading` stuck true and jam auto-load for the rest of the session.
   * Timers still fire, and getBoundingClientRect() flushes layout on its own.
   */
  #autoLoad() {
    if (!this.#active || this.#autoLoading) return;

    const grid = this.#gridList();
    if (!grid) return;

    if (this.#slotCursor >= this.#slots.length) {
      this.#stopAutoLoad();
      return;
    }

    this.#autoLoading = true;
    this.#renderClientPage(grid);
    this.#updateLoadMoreButton();
    this.#announceRender();

    window.setTimeout(() => {
      this.#autoLoading = false;

      if (this.#slotCursor >= this.#slots.length) {
        this.#stopAutoLoad();
        return;
      }

      const target = this.#resultsList?.querySelector('[ref="loadMoreContainer"]');
      if (!(target instanceof HTMLElement) || target.hidden) return;

      const { top } = target.getBoundingClientRect();
      if (top <= (window.innerHeight || 0) + 100) this.#autoLoad();
    }, 0);
  }

  #updateLoadMoreButton() {
    const container = this.#resultsList?.querySelector('[ref="loadMoreContainer"]');
    const button = container?.querySelector('[ref="loadMoreButton"]');
    if (!(container instanceof HTMLElement) || !(button instanceof HTMLButtonElement)) return;

    const hasMore = this.#renderedProducts < this.#totalProducts;
    container.hidden = !hasMore;
    container.style.display = hasMore ? "" : "none";
    button.disabled = false;
    button.textContent = "Load More";
  }

  /**
   * QA instrumentation: both orders side by side plus the cost of each stage.
   * Also parked on `window.__malachyteQA__` so the page-load summary in
   * main-collection.liquid can fold it into one comparable block.
   * @param {string[]} shopifyOrder Product ids in Shopify's own order
   * @param {string[]} malachyteOrder Product ids as ranked by Malachyte
   * @param {{liquidPages: number|null, malachyteListing: number|null, reorderRender: number|null, shimmer: number|null}} timings
   */
  #report(shopifyOrder, malachyteOrder, timings) {
    const renderedOrder = this.#slots.filter(slot => slot.isProduct).map(slot => String(slot.node.dataset.productId));

    const inCollection = new Set(shopifyOrder);
    const ranked = new Set(malachyteOrder);
    const notInCollection = malachyteOrder.filter(id => !inCollection.has(id));
    const unranked = shopifyOrder.filter(id => !ranked.has(id));
    const positionsChanged = renderedOrder.reduce(
      (count, id, index) => count + (shopifyOrder[index] === id ? 0 : 1),
      0
    );

    const stageTimings = {
      "malachyte listing (ms)": ms(timings.malachyteListing),
      "liquid pages (ms)": ms(timings.liquidPages),
      "reorder + render (ms)": ms(timings.reorderRender),
      "shimmer visible (ms)": ms(timings.shimmer),
      "liquid pages fetched": Math.max(0, this.totalPages - 1),
    };

    window.__malachyteQA__ = {
      enabled: true,
      applied: true,
      collectionId: this.collectionId,
      products: shopifyOrder.length,
      malachyteReturned: malachyteOrder.length,
      matched: malachyteOrder.length - notInCollection.length,
      notInCollection: notInCollection.length,
      unranked: unranked.length,
      positionsChanged,
      timings: stageTimings,
      orders: { shopify: shopifyOrder, malachyte: malachyteOrder, rendered: renderedOrder },
    };

    console.groupCollapsed(
      `${LOG} QA — ${positionsChanged}/${shopifyOrder.length} positions changed · shimmer ${ms(timings.shimmer)}ms`
    );
    console.log("Shopify order  :", shopifyOrder.join(", "));
    console.log("Malachyte order:", malachyteOrder.join(", "));
    console.log("Rendered order :", renderedOrder.join(", "));
    if (notInCollection.length) {
      console.warn(`${LOG} returned by Malachyte but not in this collection:`, notInCollection.join(", "));
    }
    if (unranked.length) {
      console.warn(`${LOG} in collection but unranked (appended in Shopify order):`, unranked.join(", "));
    }
    console.table(stageTimings);
    console.groupEnd();
  }

  /** @param {Record<string, unknown>} [detail] */
  #announceRender(detail = {}) {
    document.dispatchEvent(new CustomEvent("bww:rivo-wishlist-hydrate", { bubbles: true }));
    document.dispatchEvent(
      new CustomEvent("malachyte:plp-rendered", {
        detail: {
          collectionId: this.collectionId,
          page: this.#page,
          renderedCount: this.#renderedProducts,
          total: this.#totalProducts,
          ...detail,
        },
      })
    );
  }
}

if (!customElements.get("malachyte-plp")) {
  customElements.define("malachyte-plp", MalachytePlp);
}
